
/**
 * Module dependencies.
 */

import { Product as BaseProduct } from 'slyk-web-libs/types';
import { Button, Loading } from 'slyk-web-libs/design-system';
import { Container } from 'client/components/core/container';
import { DefaultProductItem } from 'client/components/core/products/default-product-item';
import { ProductItem } from 'client/components/core/products/product-item';
import { color, media } from 'react-components/styles';
import { ifProp } from 'styled-tools';
import { range } from 'lodash';
import { useTranslate } from 'slyk-web-libs/i18n';
import React, { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

/**
 * `Product` type.
 */

type Product = BaseProduct & {
  displayPrice: string;
  displayPriceConverted: string | null;
};

/**
 * `Category` type.
 */

type Category = {
  description: string;
  id: string;
  title: string;
};

/**
 * `Props` type.
 */

type Props = {
  activeCategoryId: string | null;
  categories: Array<Category>;
  isLoading: boolean;
  onActiveCategoryId: (id: string | null) => void;
  products: Array<Product>;
};

/**
 * Max items.
 */

const maxItems = 4;

/**
 * All key.
 */

const allKey = 'all';

/*
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  ${media.min('xl')`
    padding-left: 0;
    padding-right: 0;
  `}
`;

/**
 * `SliderWrapper` styled component.
 */

const SliderWrapper = styled.div<{ isDragging: boolean }>`
  -webkit-overflow-scrolling: touch;

  cursor: grab;
  display: flex;
  gap: 8px;
  margin: 32px -16px 24px;
  overflow-x: auto;
  padding: 0 16px;
  scroll-behavior: smooth;
  scrollbar-width: none;
  white-space: nowrap;

  ${ifProp('isDragging', css`
    cursor: grabbing;
  `)}

  ${media.min('ms')`
    margin-top: 56px;
  `}

  ${media.min('lg')`
    margin-top: 64px;
  `}

  ${media.min('xl')`
    margin-bottom: 32px;
    margin-top: 104px;
  `}

  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  &[aria-selected='true'] {
    background-color: ${color('gray400')};
  }
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(6, 1fr);
  margin-bottom: 56px;

  ${media.min('ms')`
    gap: 24px;
    grid-template-columns: repeat(8, 1fr);
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(12, 1fr);
    margin-bottom: 80px;
  `}

  ${media.min('xl')`
    gap: 32px;
    margin-bottom: 112px;
  `}
`;

/**
 * `EmptyItem` styled component.
 */

const EmptyItem = styled.div`
  aspect-ratio: 3 / 4;
  grid-column: 1 / span 3;
  grid-row: 1;
  position: relative;
  
  ${media.min('ms')`
    grid-column: 1 / span 2;
  `}

  ${media.min('lg')`
    grid-column: 1 / span 3;
  `}
`;

/**
 * `LoadingWrapper` styled component.
 */

const LoadingWrapper = styled.div`
  align-items: center;
  display: flex;
  grid-column: 1 / -1;
  grid-row: 1;
  justify-content: center;
`;

/**
 * `Products` styled component.
 */

const Products = styled.ul`
  display: contents;
`;

/**
 * `ProductListItem` styled component.
 */

const ProductListItem = styled.li`
  grid-column: auto / span 3;

  ${media.min('ms')`
    grid-column: auto / span 2;
  `}

  ${media.min('lg')`
    grid-column: auto / span 3;
  `}
`;

/**
 * Export `ProductsList` component.
 */

export function ProductsList(props: Props): JSX.Element {
  const { activeCategoryId, categories, isLoading, onActiveCategoryId, products } = props;
  const { translate } = useTranslate();
  const productsLength = products?.length ?? 0;
  const defaultProducts = maxItems - productsLength % maxItems;
  const showDefaultProducts = productsLength < maxItems && defaultProducts !== maxItems;
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [startX, setStartX] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const normalizedCategories = useMemo(() => {
    return [
      {
        id: allKey,
        title: translate('products.allItems')
      },
      ...categories
    ];
  }, [categories, translate]);

  const handleMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const sliderCurrent = sliderRef?.current;

    if (!sliderCurrent) {
      return;
    }

    event.preventDefault();
    setIsDragging(true);
    setStartX(event.pageX - sliderCurrent.offsetLeft);
    setScrollLeft(sliderCurrent.scrollLeft);
  }, []);

  const handleMouseMove = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const sliderCurrent = sliderRef?.current;

    if (!isDragging || !sliderCurrent) {
      return;
    }

    event.preventDefault();
    const walk = (event.pageX - sliderCurrent.offsetLeft - startX) * 2;

    sliderCurrent.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  return (
    <StyledContainer>
      <SliderWrapper
        isDragging={isDragging}
        onMouseDown={handleMouseDown}
        onMouseLeave={() => setIsDragging(false)}
        onMouseMove={handleMouseMove}
        onMouseUp={() => setIsDragging(false)}
        ref={sliderRef}
      >
        {normalizedCategories.map(({ id, title }, index) => {
          return (
            <StyledButton
              aria-selected={!activeCategoryId ? id === allKey : activeCategoryId === id}
              colorTheme={'gray'}
              key={index}
              onClick={() => onActiveCategoryId(id === allKey ? null : id)}
              role={'tab'}
              size={'medium'}
            >
              {title}
            </StyledButton>
          );
        })}
      </SliderWrapper>

      <Content>
        {isLoading ? (
          <>
            <EmptyItem />

            <LoadingWrapper>
              <Loading
                active
                relative
              />
            </LoadingWrapper>
          </>
        ) : (
          <Products>
            {products.map(product => {
              return (
                <ProductListItem key={product.id}>
                  <ProductItem product={product} />
                </ProductListItem>
              );
            })}

            {showDefaultProducts && range(defaultProducts).map((_product, index) => {
              return (
                <ProductListItem key={index}>
                  <DefaultProductItem />
                </ProductListItem>
              );
            })}
          </Products>
        )}
      </Content>
    </StyledContainer>
  );
}
